import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@/components/Icon/Icon";
import {
  findAnalyser,
  findColumnFamily as mockFindColumnFamily,
  findInstrumentCatalog as mockFindInstrumentCatalog,
  instrumentVendorAccent,
  USER_INSTRUMENTS,
} from "@/services/mock";
import type {
  AnalyserType,
  GcColumnFamily,
  InstrumentCatalogEntry,
  InstrumentVendorId,
} from "@/utils/types/instrument";

type Placement = "down" | "up";

// Minimal profile shape the picker actually reads. The full InstrumentProfile
// (mock) is structurally assignable, and a real user_exp_config DTO can be
// mapped onto it ({ id, alias:name, instrumentId:type_instrument_id,
// column:{id:type_column_id} }).
export type PickerProfile = {
  id: string;
  alias: string;
  instrumentId: number | null;
  column?: { id: number | null } | null;
};

// The two catalog lookups are injectable so a caller can supply REAL catalog
// data (useInstrumentsCatalogs) instead of the mock. They default to the mock
// helpers, so existing consumers are unchanged. findAnalyser/instrumentVendorAccent
// are pure static maps (catalog-independent) and stay imported directly.
type CatalogResolvers = {
  findInstrumentCatalog?: (id: number | null | undefined) => InstrumentCatalogEntry | null;
  findColumnFamily?: (id: number | null | undefined) => GcColumnFamily | null;
};

const vendorMonogram = (vendor: InstrumentVendorId | null | undefined): string => {
  if (vendor === "Agilent") return "A";
  if (vendor === "Thermofisher") return "T";
  return "?";
};

const vendorShortLabel = (vendor: InstrumentVendorId | null | undefined): string => {
  if (vendor === "Thermofisher") return "Thermo";
  return vendor ?? "—";
};

type InstrumentPickerProps = CatalogResolvers & {
  value: string | null;
  onChange: (id: string) => void;
  onOpenManage?: () => void;
  align?: "left" | "right";
  compact?: boolean;
  profiles?: PickerProfile[];
};

export function InstrumentPicker({
  value,
  onChange,
  onOpenManage,
  align = "left",
  compact = false,
  profiles = USER_INSTRUMENTS,
  findInstrumentCatalog = mockFindInstrumentCatalog,
  findColumnFamily = mockFindColumnFamily,
}: InstrumentPickerProps) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement>("down");
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const picked = profiles.find((p) => p.id === value) ?? null;
  const inst = picked ? findInstrumentCatalog(picked.instrumentId) : null;
  const accent = inst ? instrumentVendorAccent(inst.vendor) : "var(--line-strong)";

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const POPOVER_H = 420;
    if (spaceBelow < POPOVER_H && spaceAbove > spaceBelow) setPlacement("up");
    else setPlacement("down");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const node = wrapRef.current;
      if (node && !node.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const triggerWidth = compact ? 300 : 232;

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: triggerWidth,
          padding: "5px 8px 5px 6px",
          background: "var(--panel)",
          border: `1px solid ${open ? "var(--brand-green)" : "var(--line-strong)"}`,
          borderRadius: 5,
          boxShadow: open ? "0 0 0 3px rgba(27,58,43,0.10)" : "var(--shadow-1)",
          textAlign: "left",
          cursor: "pointer",
          transition: "border-color 120ms ease, box-shadow 120ms ease",
        }}
      >
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: 4,
            flexShrink: 0,
            background: "var(--bg-sunk)",
            border: "1px solid var(--line)",
            borderLeft: `3px solid ${accent}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--font-display)",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--ink-2)",
          }}
        >
          {inst ? vendorMonogram(inst.vendor) : "—"}
        </span>

        {picked ? (
          <div className="vstack" style={{ flex: 1, minWidth: 0, gap: 1, lineHeight: 1.2 }}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "var(--ink)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {picked.alias}
            </span>
            <span
              className="mono"
              style={{
                fontSize: 9,
                color: "var(--ink-4)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                letterSpacing: "0.02em",
              }}
            >
              {vendorShortLabel(inst?.vendor)} · {inst?.label}
            </span>
          </div>
        ) : (
          <div className="vstack" style={{ flex: 1, minWidth: 0, gap: 1, lineHeight: 1.2 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "var(--ink-3)" }}>Pick instrument…</span>
            <span className="mono" style={{ fontSize: 9, color: "var(--ink-4)", letterSpacing: "0.02em" }}>
              Required — which setup did you run?
            </span>
          </div>
        )}

        <span
          style={{
            color: "var(--ink-4)",
            flexShrink: 0,
            transform: open
              ? placement === "up"
                ? "none"
                : "rotate(180deg)"
              : placement === "up"
                ? "rotate(180deg)"
                : "none",
            transition: "transform 120ms ease",
          }}
        >
          <Icon name="chevron" size={11} />
        </span>
      </button>

      {open && (
        <InstrumentPickerPopover
          profiles={profiles}
          value={value}
          align={align}
          placement={placement}
          findInstrumentCatalog={findInstrumentCatalog}
          findColumnFamily={findColumnFamily}
          onPick={(id) => {
            onChange(id);
            setOpen(false);
          }}
          onOpenManage={() => {
            setOpen(false);
            onOpenManage?.();
          }}
        />
      )}
    </div>
  );
}

type PopoverProps = Required<CatalogResolvers> & {
  profiles: PickerProfile[];
  value: string | null;
  align: "left" | "right";
  placement: Placement;
  onPick: (id: string) => void;
  onOpenManage: () => void;
};

function InstrumentPickerPopover({
  profiles,
  value,
  align,
  placement,
  findInstrumentCatalog,
  findColumnFamily,
  onPick,
  onOpenManage,
}: PopoverProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) => {
      const inst = findInstrumentCatalog(p.instrumentId);
      const hay = `${p.alias} ${inst?.vendor ?? ""} ${inst?.label ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [profiles, query, findInstrumentCatalog]);

  return (
    <div
      className="slide-up"
      style={{
        position: "absolute",
        ...(placement === "up" ? { bottom: "calc(100% + 6px)" } : { top: "calc(100% + 6px)" }),
        ...(align === "right" ? { right: 0 } : { left: 0 }),
        zIndex: 250,
        width: 360,
        background: "var(--panel)",
        border: "1px solid var(--line-strong)",
        borderRadius: 8,
        boxShadow: "var(--shadow-3)",
        overflow: "hidden",
        fontFamily: "var(--font-sans)",
        transformOrigin: placement === "up" ? "bottom" : "top",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          background: "var(--panel-2)",
          borderBottom: "1px solid var(--line)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span className="slash-label" style={{ flex: 1 }}>
          Your registered instruments
        </span>
        <span className="mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>
          {profiles.length} profile{profiles.length === 1 ? "" : "s"}
        </span>
      </div>

      {profiles.length > 4 && (
        <div
          style={{
            padding: "8px 10px",
            borderBottom: "1px solid var(--line-2)",
            position: "relative",
          }}
        >
          <div style={{ position: "absolute", left: 18, top: 14, color: "var(--ink-4)" }}>
            <Icon name="search" size={11} />
          </div>
          <input
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search alias, model…"
            style={{ paddingLeft: 28, fontSize: 12, padding: "5px 10px 5px 28px" }}
          />
        </div>
      )}

      <div style={{ maxHeight: 320, overflowY: "auto", padding: 4 }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "28px 16px", textAlign: "center", color: "var(--ink-4)" }}>
            <div style={{ marginBottom: 6 }}>
              <Icon name="atom" size={22} />
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-2)" }}>
              {profiles.length === 0
                ? "No instruments registered yet."
                : "No instruments match your search."}
            </div>
            <div className="mono" style={{ fontSize: 10, marginTop: 4 }}>
              {profiles.length === 0
                ? "Register a profile to attribute chromatograms."
                : "Try a different term."}
            </div>
          </div>
        ) : (
          filtered.map((p) => {
            const inst = findInstrumentCatalog(p.instrumentId);
            const col = findColumnFamily(p.column?.id);
            const active = p.id === value;
            const accent = inst ? instrumentVendorAccent(inst.vendor) : "var(--line-strong)";
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onPick(p.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "8px 10px",
                  border: `1px solid ${active ? "var(--accent-line)" : "transparent"}`,
                  borderRadius: 5,
                  background: active ? "var(--accent-soft)" : "transparent",
                  textAlign: "left",
                  transition: "background 100ms ease",
                  marginBottom: 2,
                }}
                onMouseEnter={(e) => {
                  if (!active) (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-sunk)";
                }}
                onMouseLeave={(e) => {
                  if (!active) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 5,
                    flexShrink: 0,
                    background: "var(--bg-sunk)",
                    border: "1px solid var(--line)",
                    borderLeft: `3px solid ${accent}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "var(--font-display)",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--ink-2)",
                  }}
                >
                  {vendorMonogram(inst?.vendor)}
                </span>
                <div className="vstack" style={{ flex: 1, minWidth: 0, gap: 2, lineHeight: 1.25 }}>
                  <div className="hstack gap-2" style={{ minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 500,
                        color: "var(--ink)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.alias}
                    </span>
                    <AnalyserPill analyser={findAnalyser(inst?.analyser)} />
                  </div>
                  <span
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: "var(--ink-4)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {inst?.label ?? "—"} · {col?.family ?? "—"} · {inst?.platform ?? "—"}
                  </span>
                </div>
                {active && (
                  <span style={{ color: "var(--brand-green)", flexShrink: 0 }}>
                    <Icon name="check" size={14} />
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      <div
        style={{
          padding: "6px 4px",
          borderTop: "1px solid var(--line)",
          background: "var(--panel-2)",
        }}
      >
        <button
          type="button"
          onClick={onOpenManage}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            padding: "7px 10px",
            border: 0,
            borderRadius: 5,
            background: "transparent",
            textAlign: "left",
            color: "var(--ink-2)",
            cursor: "pointer",
            transition: "background 100ms ease",
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = "var(--bg-sunk)")}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = "transparent")}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 4,
              flexShrink: 0,
              background: "var(--accent-soft)",
              color: "var(--accent-ink)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid var(--accent-line)",
            }}
          >
            <Icon name="plus" size={11} />
          </span>
          <span style={{ flex: 1, fontSize: 12, fontWeight: 500 }}>Manage instruments…</span>
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            Register a new profile
          </span>
        </button>
      </div>
    </div>
  );
}

export function AnalyserPill({ analyser }: { analyser: AnalyserType | null }) {
  if (!analyser) return null;
  const isHires = analyser.resolution === "High-resolution";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 7px",
        borderRadius: 3,
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.04em",
        color: isHires ? "var(--accent-ink)" : "var(--ink-2)",
        background: isHires ? "var(--accent-soft)" : "var(--bg-sunk)",
        border: `1px solid ${isHires ? "var(--accent-line)" : "var(--line)"}`,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: 999,
          background: isHires ? "var(--brand-green)" : "var(--ink-3)",
        }}
      />
      {analyser.short}
    </span>
  );
}
